import { createSlice } from '@reduxjs/toolkit';
import {
  getCarts,
  addRentalLog,
  deleteRentalLog,
  getRentalsUnreadData,
  getCategories,
  getFacilityVendorsForFacility,
  getItems,
  getPricingTypes,
  getRentalOrders,
  getResidents,
  getVendors,
  getRentalRecipients,
  postAddNewCart,
  postAddToCart,
  postCreateResident,
  postRemoveCartItem,
  postSubmitOrderApi,
  postUpdateOrderStatus,
  postDeleteItem,
  postEditShortLongTerm,
  postEditPunchoutShortLongTerm,
  postRemovePunchoutCartItem,
  postCloseOrderItems,
  postAddItemsToOrder,
  postAddRentalNote,
  postAddRentalItemNote,
  addCopiedUsers,
  deleteCopiedUser,
  getRentalLog,
  getUOMTypes,
  postEditSerialNumber,
  postUpdateOrderPricingType,
  postUpdatePunchoutCartItemSubcategory,
  sendPickupRequest,
  postAddToPunchoutCart,
  postEditPersonFor
} from '../../api/rentals';
import { markMetricAdded, NotificationMetricType } from './notifications';

const initialState = {
  isLoading: false,
  error: null,
  unreadRentals: 0,
  data: {
    cart: [],
    items: [],
    rentalVendors: [],
    facilityVendors: [],
    badge: 0,
    categoryData: [],
    residentsList: [],
    pendingRentals: [],
    pricingTypes: [],
    currentRentals:[],
    previousRentals: [],
    rentalRecipients: [],
    rentalLog:[],
    uomTypes:[]
  },
};

const slice = createSlice({
  name: 'rentals',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    addToCartSuccess(state, action) {
      state.isLoading = false;
      state.data.badge = 1;
      state.data.cart = action.payload;
      state.data.cart.map((rentalItem) => {
        rentalItem.isSelected = true;
        return rentalItem;
      });
    },
    getCartSuccess(state, action) {
      state.isLoading = false;
      const cartWithSelection = action.payload.map((rentalItem) => ({
        ...rentalItem, isSelected: true
      }));
      state.data.cart = cartWithSelection;
    },
    getAddItemsToOrderSuccess(state, action) {
      state.isLoading = false;
    },
    addCopiedUsersSuccess(state, action) {
      const { copiedUsers, orderId, orderStatus } = action.payload;  
      if(orderStatus.toUpperCase() === "PENDING") {
        const order = state.data.pendingRentals.find(item => item.orderId === orderId);
        if (order) order.ccUsers = [...order.ccUsers, ...copiedUsers];
      }
      else if(orderStatus.toUpperCase() === "CURRENT") {
        const order = state.data.currentRentals.find(item => item.orderId === orderId);
        if (order) order.ccUsers = [...order.ccUsers, ...copiedUsers];
      }
      else {
        const order = state.data.previousRentals.find(item => item.orderId === orderId);
        if (order) order.ccUsers = [...order.ccUsers, ...copiedUsers];
      }
     
    },
    deleteCopiedUserSuccess(state, action) {
       const { copiedUser, orderId, orderStatus } = action.payload; 
       if(orderStatus.toUpperCase() === "PENDING") {
         const order = state.data.pendingRentals.find(item => item.orderId === orderId);
         if (order && Array.isArray(order.ccUsers)) {
           order.ccUsers = order.ccUsers.filter((user) => user.email !== copiedUser);
         }
       }
       else if(orderStatus.toUpperCase() === "CURRENT") {
         const order = state.data.currentRentals.find(item => item.orderId === orderId);
         if (order && Array.isArray(order.ccUsers)) {
           order.ccUsers = order.ccUsers.filter((user) => user.email !== copiedUser);
         }
       }
       else {
         const order = state.data.previousRentals.find(item => item.orderId === orderId);
         if (order && Array.isArray(order.ccUsers)) {
           order.ccUsers = order.ccUsers.filter((user) => user.email !== copiedUser);
         }
       }
    },
    editPersonForSuccess(state, action) {
      const { orderId, resident } = action.payload;
      const personFor = `${resident.firstName} ${resident.lastName}`;
      const order =
        state.data.pendingRentals.find((item) => item.orderId === orderId) ??
        state.data.currentRentals.find((item) => item.orderId === orderId) ??
        state.data.previousRentals.find((item) => item.orderId === orderId);
      if (order) {
        order.personFor = personFor;
        order.residentId = resident.id;
        order.room = resident.room;
        order.residentDeleteFlag = false;
      }
    },
    removeFromReduxCart(state, action) {
      const { cart } = state.data;
      const { cartItemId, deleteAll } = action.payload;
      const index = cart?.findIndex((rentalItem) => cartItemId === rentalItem.cartItemId);
      if (cart[index].quantity === 1 || deleteAll) {
        cart?.splice(index, 1);
      } else cart[index].quantity--;
    },
    updateCartItemPricingTypeSuccess(state, action) {
      const updatedCartItem = action.payload;
      const cartItemIndex = state.data.cart.findIndex((item) => item.cartItemId === updatedCartItem.cartItemId);
      if (cartItemIndex >= 0) {
        state.data.cart[cartItemIndex] = {
          ...updatedCartItem,
          isSelected: state.data.cart[cartItemIndex].isSelected
        };
      }
    },
    updatePunchoutCartItemSubcategorySuccess(state, action) {
      const updatedCartItem = action.payload;
      const cartItemIndex = state.data.cart.findIndex((item) => item.cartItemId === updatedCartItem.cartItemId);
      if (cartItemIndex >= 0) {
        state.data.cart[cartItemIndex] = {
          ...updatedCartItem,
          isSelected: state.data.cart[cartItemIndex].isSelected
        };
      }
    },
    postEditSerialNumberSuccess(state, action) {
      const { cartItemId, newSerialNumber, isPunchoutOrder, response } = action.payload;
      if(response.success) {
        const rentalOrders = [state.data.pendingRentals, state.data.currentRentals, state.data.previousRentals];
        rentalOrders.forEach((orders) => {
          const cartItem = orders?.find((item) =>
            item.cartItemId === cartItemId && (isPunchoutOrder ? item.itemId === 0 : item.itemId !== 0)
          );
          if (cartItem) {
            cartItem.serialNumber = newSerialNumber;
          }
        });

        const cartItem = state.data.cart?.find((item) => item.cartItemId === cartItemId);
        if (cartItem) {
          cartItem.serialNumber = newSerialNumber;
        }
      }
    },
    getUOMTypeSuccess(state, action) {
      state.isLoading = false;
      state.data.uomTypes = action.payload;
    },
    removeFromReduxOrder(state, action) {
      const { cartItemId, orderStatus } = action.payload;
      if(orderStatus.toUpperCase() === 'PENDING') {
      state.data.pendingRentals = state.data.pendingRentals.filter((item) => item.cartItemId !== cartItemId);
      }
      else if(orderStatus.toUpperCase() === 'CURRENT') {
      state.data.currentRentals = state.data.currentRentals.filter((item) => item.cartItemId !== cartItemId);   
      }
      else {
      state.data.previousRentals = state.data.previousRentals.filter((item) => item.cartItemId !== cartItemId);
      }
    },
      
    clearBadge(state, action) {
      state.data.badge = 0;
    },
     
    clearFacilityVendors(state, action) {
      state.data.facilityVendors = [];
    },
    getRentalLogSuccess(state, action) {
      state.isLoading = false;
      state.data.rentalLog = action.payload;
    },
    addRentalLogSuccess(state, action) {
      state.isLoading = false;
      state.data.rentalLog = action.payload;
    },

    deleteRentalLogMessageSuccess(state, action) {
      state.isLoading = false;
      state.data.rentalLog = action.payload;
    },

    selectedItem(state, action) {
      const { cart } = state.data;
      const { cartItem, isSelected } = action.payload;
      const index = cart?.findIndex(
        (rentalItem) => cartItem.vendorItemId === rentalItem.vendorItemId && cartItem.residentId === rentalItem.residentId
      );
      cart[index].isSelected = !cart[index].isSelected;
    },
    selectResidentCartItems(state, action) {
      const { cart } = state.data;
      const residentId = action.payload;
      cart.map((rentalItem) => {
        if (residentId === rentalItem.residentId) {
          rentalItem.isSelected = true;
        }
        return rentalItem;
      });
    },
    deselectResidentCartItems(state, action) {
      const { cart } = state.data;
      const residentId = action.payload;
      cart.map((rentalItem) => {
        if (residentId === rentalItem.residentId) {
          rentalItem.isSelected = false;
        }
        return rentalItem;
      });
    },
    getItemsSuccess(state, action) {
      state.isLoading = false;
      state.data.items = action.payload;
    },
    getCategoriesSuccess(state, action) {
      state.isLoading = false;
      state.data.categoryData = action.payload;
    },
    getVendorsSuccess(state, action) {
      state.isLoading = false;
      state.data.rentalVendors = action.payload;
    },
    getResidentsSuccess(state, action) {
      state.isLoading = false;
      state.data.residentsList = action.payload;
    },
    getFacilityVendorsSuccess(state, action) {
      state.isLoading = false;
      state.data.facilityVendors = action.payload;
    },
    postCreateResidentSuccess(state, action) {
      state.isLoading = false;
      state.data.residentsList = [...state.data.residentsList, action.payload];
    },
    getRentalRecipientsSuccess(state, action) {
      state.isLoading = false;
      state.data.rentalRecipients = action.payload;
    },
    postSubmitOrderSuccess(state, action) {
      state.isLoading = false;
    },
    getPendingRentalsSuccess(state, action) {
     state.isLoading = false;
     if (action.payload.orderStatus.toUpperCase() === 'PENDING') state.data.pendingRentals = action.payload.response 
     else if (action.payload.orderStatus.toUpperCase() === 'CURRENT') state.data.currentRentals = action.payload.response 
     else state.data.previousRentals = action.payload.response;
     
    },
    postUpdateOrderStatusSuccess(state, action) {
      state.isLoading = false;
    },

    addLinkedTickets(state, action) {
      const { orderId, idToAdd, orderStatus } = action.payload;
        if (orderStatus.toUpperCase() === 'PENDING') {
          const order = state.data.pendingRentals.find(item => item.orderId === orderId)
          if (order) order.ticketIds = [...order.ticketIds, idToAdd];
        }
        else if (orderStatus.toUpperCase() === 'CURRENT') {    
          const order = state.data.currentRentals.find(item => item.orderId === orderId)
          if (order) order.ticketIds = [...order.ticketIds, idToAdd];
        }
        else {
          const order = state.data.previousRentals.find(item => item.orderId === orderId)
          if (order) order.ticketIds = [...order.ticketIds, idToAdd];
        }
      state.isLoading = false;
    },
    closeOrderItemsSuccess(state) {
      state.isLoading = false;
    },
    pickupRequestSuccess(state, action) {
      const { orderCartItemIds, userEmail } = action.payload;
      const requestedPickupOn = new Date().toISOString().slice(0, 19);
      state.data.currentRentals
        .filter(item => orderCartItemIds.includes(item.cartItemId))
        .forEach(item => {
          item.requestedPickupOn = requestedPickupOn;
          item.requestedPickupBy = userEmail;
        });
      state.isLoading = false;
    },
    vendorPickupConfirmed(state, action) {
      const { orderCartItemIds, userEmail } = action.payload;
      const closedOn = new Date().toISOString().slice(0, 19);
      state.data.currentRentals
        .filter(item => orderCartItemIds.includes(item.cartItemId))
        .forEach(item => {
          item.status = 'Closed';
          item.vendorStatus = 'Picked Up';
          item.closedOn = closedOn;
          item.closedBy = userEmail;
        });
      state.isLoading = false;
    },
    getPricingTypesSuccess(state, action) {
      state.isLoading = false;
      state.data.pricingTypes = action.payload;
    },
    removeFromReduxItems(state, action) {
      const itemId = action.payload;
      state.data.items = state.data.items.filter((item) => item.id !== itemId);
      const index = state.data.cart?.findIndex((rentalItem) => itemId === rentalItem.itemId);
      state.data.cart?.splice(index, 0);
    },
    postEditShortLongTermSuccess(state) {
      state.isLoading = false;
    },
    postRentalNoteMessageSuccess(state, action) {
      const { orderId, message, orderStatus } = action.payload;
      if(orderStatus.toUpperCase() === 'PENDING')
        state.data.pendingRentals.find(item => item.orderId === orderId).note = message
      else if(orderStatus.toUpperCase() === 'CURRENT')
        state.data.currentRentals.find(item => item.orderId === orderId).note = message
      else
        state.data.previousRentals.find(item => item.orderId === orderId).note = message
    },
    postRentalNoteItemMessageSuccess(state, action) {
       const { cartItemId, message, orderStatus } = action.payload;

        if(orderStatus.toUpperCase() === 'PENDING') {
          state.data.pendingRentals.find(item => item.cartItemId === cartItemId).itemNote = message;
        } else if(orderStatus.toUpperCase() === 'CURRENT') {    
          state.data.currentRentals.find(item => item.cartItemId === cartItemId).itemNote = message;
        } else {
          state.data.previousRentals.find(item => item.cartItemId === cartItemId).itemNote = message
        }
      },
    updateOrderItemStatus(state, action) {
      const { pendingRentals } = state.data;
      const { itemId, isChecked } = action.payload;
      const itemIndex = pendingRentals.findIndex((item) => item.cartItemId === itemId);
      if (itemIndex !== -1 && !isChecked) {
        pendingRentals[itemIndex] = {
          ...pendingRentals[itemIndex],
          rejectedOn: new Date().toISOString().slice(0, 19),
        };
      } else {
        pendingRentals[itemIndex] = {
          ...pendingRentals[itemIndex],
          rejectedOn: null,
        };
      }
      state.isLoading = false;
    },
    getUnreadRentalsCountSuccess(state, action) {
      const { unreadRentals } = action.payload;
      state.unreadRentals = unreadRentals;
    },
  },
});

export function setUnreadRentalsCount(unreadRentals) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.getUnreadRentalsCountSuccess({ unreadRentals }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUnreadRentalCount() {
  return async (dispatch) => {
    try {
      const response = await getRentalsUnreadData();
      dispatch(slice.actions.getUnreadRentalsCountSuccess({ unreadRentals: response.data }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function addItemToCart(itemId, residentId, userId, cartId, pricingId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      if (cartId === 0) {
        const response = await postAddNewCart(residentId, userId);
        const cartId = response.data.id;
        const resp = await postAddToCart(itemId, cartId, userId, pricingId);
        if (resp.status === 200) {
          dispatch(slice.actions.addToCartSuccess(resp.data));
        }
      } else {
        const resp = await postAddToCart(itemId, cartId, userId, pricingId);
        if (resp.status === 200) {
          dispatch(slice.actions.addToCartSuccess(resp.data));
        }
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function addItemToPunchoutCart(cartItemId, residentId, userId, cartId, pricingId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
        const resp = await postAddToPunchoutCart(cartItemId, cartId, userId, pricingId);
        if (resp.status === 200) {
          dispatch(slice.actions.addToCartSuccess(resp.data));
        }
      } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getCartsForUser() {
  return async (dispatch) => {
    try {
      const response = await getCarts();
      dispatch(slice.actions.getCartSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getBaseUomTypes(){
  return async (dispatch) => {
    try {
      const response = await getUOMTypes();
      dispatch(slice.actions.getUOMTypeSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function editSerialNumber(cartItemId, newSerialNumber, isPunchoutOrder) {
  return async (dispatch) => {
    try {
      const response = await postEditSerialNumber(cartItemId, newSerialNumber, isPunchoutOrder);
      dispatch(slice.actions.postEditSerialNumberSuccess({cartItemId, newSerialNumber, isPunchoutOrder, response}));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return false;
    }
  };
}

export function addItemsToOrder(items, orderId, orderStatus) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postAddItemsToOrder(items, orderId, orderStatus);
      dispatch(getRentalOrdersRedux(orderStatus));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return false;
    }
  };
}

export function removeItemFromCart(cartItemId, deleteAll) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postRemoveCartItem(cartItemId, deleteAll);

      if (response.status === 200) {
        dispatch(slice.actions.removeFromReduxCart({cartItemId, deleteAll}));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function removePunchoutItemFromCart(cartItemId, deleteAll) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postRemovePunchoutCartItem(cartItemId, deleteAll);

      if (response.status === 200) {
        dispatch(slice.actions.removeFromReduxCart({cartItemId, deleteAll}));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function clearBadgeFromRedux() {
  return async (dispatch) => {
    dispatch(slice.actions.clearBadge());
  };
}

export function clearFacilityVendors() {
  return async (dispatch) => {
    dispatch(slice.actions.clearFacilityVendors());
  };
}

export function updateSelectedItem(cartItem, isSelected) {
  return async (dispatch) => {
    dispatch(slice.actions.selectedItem({ cartItem, isSelected }));
  };
}

export function deselectAllItems(residentId) {
  return async (dispatch) => {
    dispatch(slice.actions.deselectResidentCartItems(Number(residentId)));
  };
}
export function selectAllItems(residentId) {
  return async (dispatch) => {
    dispatch(slice.actions.selectResidentCartItems(Number(residentId)));
  };
}
export function getRentalItems() {
  return async (dispatch) => {
    try {
      const response = await getItems();
      dispatch(slice.actions.getItemsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getCategoryData() {
  return async (dispatch) => {
    try {
      const response = await getCategories();
      dispatch(slice.actions.getCategoriesSuccess(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getRentalVendors() {
  return async (dispatch) => {
    try {
      const response = await getVendors();
      dispatch(slice.actions.getVendorsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getResidentsForFacility(facilityId = 0) {
  return async (dispatch) => {
    try {
      const response = await getResidents(facilityId);
      dispatch(slice.actions.getResidentsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function getFacilityVendors(facilityId = 0) {
  return async (dispatch) => {
    try {
      const response = await getFacilityVendorsForFacility(facilityId);
      dispatch(slice.actions.getFacilityVendorsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function postAddResident(resident, facilityId) {
  return async (dispatch) => {
    try {
      const response = await postCreateResident(resident, facilityId);

      dispatch(slice.actions.postCreateResidentSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
    return false;
  };
}
export function postSubmitOrder(items, isPunchoutOrder) {
  return async (dispatch) => {
    try {
      const response = await postSubmitOrderApi(items, isPunchoutOrder);
      dispatch(slice.actions.postSubmitOrderSuccess(items));
      dispatch(getCartsForUser());
      const orderId = response?.data?.orderId;
      if (orderId) dispatch(markMetricAdded(NotificationMetricType.RentalsSubmitted, orderId));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return false;
    }
  };
}
export function postAddCopiedUsers(copiedUsers, orderId, orderStatus) {
 return async (dispatch) => {
    try {
      const response = await addCopiedUsers(copiedUsers, orderId);
       dispatch(slice.actions.addCopiedUsersSuccess({copiedUsers, orderId, orderStatus}));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
  };
}
export function postDeleteCopiedUser(copiedUser, orderId, orderStatus) {
  return async (dispatch) => {
    try {
      const response = await deleteCopiedUser(copiedUser, orderId);
       dispatch(slice.actions.deleteCopiedUserSuccess({copiedUser, orderId, orderStatus}));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
  };
}

export function editPersonFor(orderId, residentId) {
  return async (dispatch) => {
    try {
      const response = await postEditPersonFor(orderId, residentId);
      if (response?.success) {
        dispatch(slice.actions.editPersonForSuccess({ orderId, resident: response.data }));
      }
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
  };
}

export function getRentalOrdersRedux(orderStatus) {
 return async (dispatch) => {
    try {
      const response = await getRentalOrders(orderStatus);
      if(Array.isArray(orderStatus)) { 
        orderStatus.forEach((status)  => {
          dispatch(slice.actions.getPendingRentalsSuccess({response: response.data[status.toUpperCase()], orderStatus: status}))}
        )
      }
      else {
         dispatch(slice.actions.getPendingRentalsSuccess({response: response.data[orderStatus.toUpperCase()], orderStatus}));
      }
       return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return false;
    }
  };
}

export function addNewRentalNote({ message, orderId, orderStatus}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
     try {
      const response = await postAddRentalNote({ message, orderId });
      dispatch(slice.actions.postRentalNoteMessageSuccess({ ...response, orderId, message, orderStatus }));
    } catch (error) {
      console.error({ error });
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function addNewRentalItemNote({ message, cartItemId, orderStatus}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
     try {
      const response = await postAddRentalItemNote({ message, cartItemId });
      dispatch(slice.actions.postRentalNoteItemMessageSuccess({ ...response, cartItemId, message, orderStatus }));
    } catch (error) {
      console.error({ error });
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateOrderStatus(orderItems, isOrderRejected, orderId, isPunchoutOrder) {
  return async (dispatch) => {
    try {
      const response = await postUpdateOrderStatus(orderItems, isOrderRejected, orderId, isPunchoutOrder);
      if (response.data.success) {
        dispatch(slice.actions.postUpdateOrderStatusSuccess(response.data));
        dispatch(getRentalOrdersRedux(['CURRENT','PENDING']));
      }
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
  };
}
export function closeOrderItems(orderCartItemIds, orderId, isPunchoutOrder) {
  return async (dispatch) => {
    try {
      const response = await postCloseOrderItems(orderCartItemIds, orderId, isPunchoutOrder);
      if (response.data.success === true) {
        dispatch(slice.actions.closeOrderItemsSuccess(response.data));
        dispatch(getRentalOrdersRedux(['CURRENT','PREVIOUS']));
        return response;
      }
      return null;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
  };
}
export function pickupOrderItemsRequest(orderCartItemIds, orderId, userEmail, isPunchoutOrder) {
  return async (dispatch) => {
    try {
      const response = await sendPickupRequest(orderCartItemIds, orderId, isPunchoutOrder);
      if (response.success === true) {
        const vendorPickedUpCartItemIds = response.vendorPickedUpCartItemIds || [];
        const pickupRequestedCartItemIds = orderCartItemIds
          .filter(cartItemId => !vendorPickedUpCartItemIds.includes(cartItemId));

        if (vendorPickedUpCartItemIds.length > 0) {
          dispatch(slice.actions.vendorPickupConfirmed({ orderCartItemIds: vendorPickedUpCartItemIds, userEmail }));
        }
        if (pickupRequestedCartItemIds.length > 0) {
          dispatch(slice.actions.pickupRequestSuccess({ orderCartItemIds: pickupRequestedCartItemIds, userEmail }));
        }
        return response;
      }
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error?.response?.data || { success: false, message: error?.message || 'Error requesting pickup' };
    }
  };
}
export function getPricingTypeOptions() {
  return async (dispatch) => {
    try {
      const response = await getPricingTypes();
      dispatch(slice.actions.getPricingTypesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getRentalLogRedux(rentalId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await getRentalLog(rentalId);
      dispatch(slice.actions.getRentalLogSuccess(response));
      return 'success';
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
export function addRentalLogRedux({rentalId, messageType}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await addRentalLog(rentalId ,messageType) 
      dispatch(slice.actions.addRentalLogSuccess(response));
      return 'success';
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
export function deleteRentalLogRedux(logId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await deleteRentalLog(logId);
      dispatch(slice.actions.deleteRentalLogMessageSuccess(response));
      return 'success';
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}
export function getRentalRecipientsRedux(orderId) {
  return async (dispatch) => {
    try {
      const response = await getRentalRecipients(orderId);
      dispatch(slice.actions.getRentalRecipientsSuccess(response));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function deleteItem(itemId, enqueueSnackbar) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await postDeleteItem(itemId);

      if (response.status === 200) {
        dispatch(slice.actions.removeFromReduxItems(itemId));
        dispatch(getCartsForUser());
        enqueueSnackbar('Item deleted', { variant: 'success' });
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function updateShortLongTerm(cartItemId, isPunchoutOrder) {
  return async (dispatch) => {
    try {
      const response = await postEditShortLongTerm(cartItemId, isPunchoutOrder);
      dispatch(slice.actions.updateShortLongTermSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function updatePunchoutShortLongTerm(cartItemId) {
  return async (dispatch) => {
    try {
      const response = await postEditPunchoutShortLongTerm(cartItemId);
      dispatch(slice.actions.updateShortLongTermSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
export function updateOrderItemInRedux(itemId, isChecked) {
  return async (dispatch) => {
    dispatch(slice.actions.updateOrderItemStatus({ itemId, isChecked }));
  };
}
export function updateCartItemPricingType(cartItemId, pricingTypeId) {
  return async (dispatch) => {
    try {
      const response = await postUpdateOrderPricingType(cartItemId, pricingTypeId);
      if (response.data.success) dispatch(slice.actions.updateCartItemPricingTypeSuccess(response.data.data));
      return response;
    }
    catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
  };
}

export function updatePunchoutCartItemSubcategory(cartItemId, subcategoryId) {
  return async (dispatch) => {
    try {
      const response = await postUpdatePunchoutCartItemSubcategory(cartItemId, subcategoryId);
      if (response.data.success) dispatch(slice.actions.updatePunchoutCartItemSubcategorySuccess(response.data.data));
      return response;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return null;
    }
  };
}
export function addLinkedTicketToRental({ orderId, idToAdd, orderStatus }) { 
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    dispatch(slice.actions.addLinkedTickets({ orderId, idToAdd, orderStatus }));
  }
};
export default slice.reducer;
